import { animated, easings, useSpring } from '@react-spring/web';
import classNames from 'classnames';
import { useEffect, useRef } from 'react';
import { useLatest } from 'react-use';
import styled from 'styled-components';

import { CardImg } from '@sorare/core/src/components/card/CardImg';

const Wrapper = styled(animated.div)`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  aspect-ratio: var(--card-aspect-ratio);
  border: 1px dashed var(--c-nd-200);
  border-radius: var(--double-unit);
`;
const CardImgWrapper = styled.div`
  width: 70%;
  position: relative;
  filter: brightness(50%);
`;

type Props = {
  cardPictureUrl: string;
  isNewCardSlot: boolean;
  shouldAnimate: boolean;
  scrollContainerRef: React.RefObject<HTMLElement | null>;
  onScroll: (itemHeight: number) => Promise<void>;
};

export const EmptyCollectionSlot = ({
  cardPictureUrl,
  isNewCardSlot,
  shouldAnimate,
  scrollContainerRef,
  onScroll,
}: Props) => {
  const ref = useRef<HTMLDivElement>(null);
  const [styles, api] = useSpring(() => ({
    from: {
      opacity: 0,
      scale: 1.3,
      brightness: 1.2,
      border: 1,
    },
  }));

  const latestOnScroll = useLatest(onScroll);
  useEffect(() => {
    (async () => {
      if (shouldAnimate && ref.current) {
        await latestOnScroll.current(
          ref.current.getBoundingClientRect().height
        );
        api.start({
          opacity: 1,
          scale: 1,
          brightness: 1,
          border: 0,
          config: {
            duration: 500,
            easing: easings.easeOutCubic,
          },
        });
      }
    })();
  }, [shouldAnimate, api, scrollContainerRef, latestOnScroll]);

  return (
    <Wrapper
      ref={ref}
      className={classNames({ isNewCardSlot })}
      style={{
        borderWidth: styles.border.to(border => `${border}px`),
      }}
    >
      {isNewCardSlot ? (
        <animated.div
          style={{
            opacity: styles.opacity,
            scale: styles.scale,
            filter: styles.brightness.to(
              brightness => `brightness(${brightness})`
            ),
          }}
        >
          <CardImg src={cardPictureUrl} width={320} />
        </animated.div>
      ) : (
        <CardImgWrapper>
          <CardImg src={cardPictureUrl} width={320} />
        </CardImgWrapper>
      )}
    </Wrapper>
  );
};
